import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { EnvironmentService } from '@app/core/environment.service';
import {
  ConsentConfig,
  ConsentPurpose,
  CountryConsent,
  CountryConsentFormGroup,
} from '@app/core/models';
import { isCountryConsent } from '@app/core/services/utils/consents';
import { DetailTermsComponent } from '@app/shared/components/detail-terms';
import { RcCheckboxComponent } from '@app/shared/components/rc-checkbox/rc-checkbox.component';
import { MarkdownLinkPipe } from '@app/shared/pipes/markdown-link/markdown-link.pipe';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-consents-form',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DetailTermsComponent,
    RcCheckboxComponent,
    TranslateModule,
    MarkdownLinkPipe,
  ],
  templateUrl: './consents-form.component.html',
  styleUrl: './consents-form.component.scss',
})
export class ConsentsFormComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly environmentService = inject(EnvironmentService);

  form = input.required<CountryConsentFormGroup>();
  countryCodeFormControl = input.required<FormControl<string>>();

  readonly consentsConfig = signal<ConsentConfig[]>([]);

  readonly hasAnyRequiredConsent = computed(() => {
    return this.consentsConfig()
      .filter(isCountryConsent)
      .some((consent) => {
        return consent.consentPurposes.some((purpose) => {
          return purpose.consentRequired === true;
        });
      });
  });

  readonly isCountryConsent = isCountryConsent;

  ngOnInit() {
    this.subscribeToCountryCodeChanges();
  }

  private createConsentFormControls(consent: CountryConsent) {
    const formControls = consent.consentPurposes.map((consentPurpose) => {
      const fc = new FormControl<boolean>(
        {
          value: !!consentPurpose.isApproved,
          disabled: consentPurpose.allowApproval === false,
        },
        {
          validators: consentPurpose.consentRequired
            ? Validators.requiredTrue
            : null,
          nonNullable: true,
        },
      );

      return { purposeId: consentPurpose.purposeId, fc };
    });

    if (
      consent.consentPurposes.some(
        (consentPurpose) => consentPurpose.sharedCheckboxId,
      )
    ) {
      const checkboxId: string[] = [];
      const groupedConsents = consent.consentPurposes.filter(
        (consentPurpose) => consentPurpose.sharedCheckboxId,
      );

      groupedConsents.forEach((consentPurpose) => {
        if (consentPurpose.sharedCheckboxId) {
          if (checkboxId.includes(consentPurpose.sharedCheckboxId)) {
            return;
          }

          checkboxId.push(consentPurpose.sharedCheckboxId);

          const sharedConsents = groupedConsents.filter(
            (c) => c.sharedCheckboxId === consentPurpose.sharedCheckboxId,
          );
          const linkedConsentsPurposeIds = sharedConsents
            .slice(1)
            .map((c) => c.purposeId);

          const linkedFormControls = formControls
            .filter((c) => linkedConsentsPurposeIds.includes(c.purposeId))
            .map((c) => c.fc);

          linkedFormControls.forEach((linkedFc) => {
            linkedFc.disable();
          });

          formControls
            .find((c) => c.purposeId === sharedConsents[0].purposeId)!
            .fc.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((value) => {
              linkedFormControls.forEach((linkedFc) => {
                linkedFc.setValue(value);
              });
            });
        }
      });
    }

    return formControls;
  }

  getGroupedCountryConsentBySharedCheckbox(consent: CountryConsent) {
    const result: {
      sharedCheckboxId: string;
      consentPurposes: ConsentPurpose[];
    }[] = [];

    consent.consentPurposes.forEach((consentPurpose, index) => {
      const existingGroup = result.find(
        (group) => group.sharedCheckboxId === consentPurpose.sharedCheckboxId,
      );

      if (existingGroup) {
        existingGroup.consentPurposes.push(consentPurpose);
        return;
      }

      result.push({
        sharedCheckboxId: consentPurpose.sharedCheckboxId || index.toString(),
        consentPurposes: [consentPurpose],
      });
    });

    return result;
  }

  private subscribeToCountryCodeChanges() {
    this.countryCodeFormControl()
      .valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((countryCode) => {
        const form = this.form();

        Object.keys(form.controls).forEach((key) => {
          (form as FormGroup).removeControl(key);
        });

        if (countryCode) {
          const consents =
            this.environmentService.getCountryConsents(countryCode);

          const consentsWithPurpose = consents.filter((consent) =>
            isCountryConsent(consent),
          );

          consentsWithPurpose.forEach((consent) => {
            const formControls = this.createConsentFormControls(consent);

            if (consent.consentPurposes)
              formControls.forEach(({ purposeId, fc }) => {
                form.addControl(purposeId, fc);
              });
          });

          this.consentsConfig.set(consents);
        } else {
          this.consentsConfig.set([]);
        }
      });
  }
}
